import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';

import { Trans } from 'react-i18next';
import { FETCH_APP_DATA } from '../../graphql';
import { Header } from '../Header';
import { GlassesTypeSelector } from '../GlassesTypeSelector';
import { GlassesPackageSelector } from '../GlassesPackageSelector';
import { Container } from '../Container';
import { GlassesPackageView } from '../GlassesPackageView';
import { BackgroundColorWrapper } from '../BackgroundColorWrapper';
import { Footer } from '../Footer';
import { getQSVar } from '../../utils';
import { IAddOn, IFeature, IGlassesPackage, IQueryData } from '../../types';

export const Ssp = () => {
    const [showMultifocal, setShowMultifocal] = useState(
        getQSVar('template') === 'multifocal'
    );
    const [displayedPackages, setDisplayedPackages] = useState<
        IGlassesPackage[] | undefined
    >([]);
    const [selectedPackage, setSelectedPackage] = useState<
        IGlassesPackage | undefined
    >();
    const [displayedFeatures, setDisplayedFeatures] = useState<
        IFeature[] | undefined
    >([]);
    const [displayedAddOns, setDisplayedAddOns] = useState<IAddOn[]>([]);
    const [selectedAddOns, setSelectedAddOns] = useState<IAddOn[]>([]);
    const lng = getQSVar('lng') || 'nl';
    const { data } = useQuery<IQueryData>(FETCH_APP_DATA, {
        variables: {
            lng,
        },
    });

    // Filter and set available packages based on multifocal setting
    useEffect(() => {
        if (!data) return;

        const displayedGlassesPackages = data.glassesPackages
            .filter(
                (glassesPackage) => glassesPackage.multifocal === showMultifocal
            )
            .sort((a, b) => a.order - b.order);

        const displayedPackageFeatures = data.features
            .filter((feature) => feature.multifocalOnly === showMultifocal)
            .sort((a, b) => a.order - b.order);

        setDisplayedPackages(displayedGlassesPackages);
        setDisplayedFeatures(displayedPackageFeatures);
    }, [data, showMultifocal]);

    // Set selected package when packages are loaded or when multifocal settings change
    useEffect(() => {
        if (!displayedPackages) return;

        const newSelectedPackage =
            displayedPackages.find((glassesPackage) => {
                // Find package with same name when switching multifocal setting
                if (selectedPackage) {
                    return selectedPackage.name === glassesPackage.name;
                }

                return glassesPackage.default;
            }) || displayedPackages[1];

        setSelectedPackage(newSelectedPackage);
    }, [displayedPackages, selectedPackage]);

    // Set diplayed addOns when packages are loaded or when selected package changes
    useEffect(() => {
        if (!selectedPackage) return;

        // Add-on is free if package is included
        const addOns = selectedPackage.addOns.map((addOn) => ({
            ...addOn,
            priceInt: addOn.freeWithPackagesIds.includes(selectedPackage.id)
                ? 0
                : addOn.priceInt,
        }));

        setDisplayedAddOns(addOns);
    }, [selectedPackage]);

    // Set selected addOn when packages are loaded or when selected package changes
    useEffect(() => {
        if (!selectedPackage) return;

        setSelectedAddOns([]);
    }, [selectedPackage]);

    const handleAddOnClick = (addOn: IAddOn) => {
        const addOnIsSelected = selectedAddOns
            .map((selectedAddOn) => selectedAddOn.id)
            .includes(addOn.id);

        // Deselect addOn if already selected, keep any other selected addOns
        if (addOnIsSelected) {
            setSelectedAddOns(
                selectedAddOns.filter(
                    (selectedAddOn) => selectedAddOn.id !== addOn.id
                )
            );

            return;
        }

        // In all other cases
        setSelectedAddOns([...selectedAddOns, addOn]);
    };

    const contactUrl =
        lng === 'fr'
            ? 'https://www.pearle.be/fr_BE/accueil/contact'
            : 'https://www.pearle.be/nl_BE/home/contact';

    if (!data || !displayedPackages || !selectedPackage || !displayedFeatures)
        return null;

    return (
        <Container>
            <Header />
            <GlassesTypeSelector
                selectedPackage={selectedPackage}
                setShowMultifocal={setShowMultifocal}
                showMultifocal={showMultifocal}
            />
            <BackgroundColorWrapper
                color={selectedPackage.backgroundColorGeneral}
            >
                <GlassesPackageSelector
                    packages={displayedPackages}
                    selectedPackage={selectedPackage}
                    onPackageClick={setSelectedPackage}
                />
                <GlassesPackageView
                    addOns={displayedAddOns}
                    allFeatures={displayedFeatures}
                    selectedAddOns={selectedAddOns}
                    selectedPackage={selectedPackage}
                    onAddOnClick={handleAddOnClick}
                />
                <Footer>
                    <Trans i18nKey="footer">
                        Let op: je kunt online een bril bestellen met diverse
                        glaspakketten en glasopties, maar niet alle extra
                        glasopties zijn mogelijk bij een online bestelling.
                        Vragen?
                        <a href={contactUrl} rel="noreferrer" target="_blank">
                            Neem contact met ons op
                        </a>
                        .
                    </Trans>
                </Footer>
            </BackgroundColorWrapper>
        </Container>
    );
};
